import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Table,
  TableHead,
  TableContainer,
  Tooltip,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Modal,
  IconButton,
  InputAdornment,
  Typography,
  Container,
  CircularProgress,
  Fade,
  Box, // Added Box to the imports
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useParams } from "react-router-dom";
import base64 from "base64-js";


const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});



const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  background: "linear-gradient(145deg, #ffffff, #f9f9f9)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

function LessonPage() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [lesson, setLesson] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lessonIdToDelete, setLessonIdToDelete] = useState("");
  const navigate = useNavigate();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }), // replace with actual formId if hardcoded
      });

      const result = await response.json();
      const data = result[0];

      // setAuthorization(data);
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  const Fetchlesson = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/OfflineLessoon/GetLesson`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Error fetching lesson: ${response.status}`);
      }

      const data = await response.json();

      if (data.data === null && data.msg === "Record Not Found") {
        setLesson([]); // Set empty array to show "No records found" in table
        return;
      }

      setLesson(data);
    } catch (error) {
      console.error("API request error:", error);
      setLesson([]); // Set empty array on error to show "No records found"
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    Fetchlesson();
    Authorizer();
  }, [Fetchlesson]);

  const handleEditLesson = (lessonId) => {
    navigate(`/updatelesson/${lessonId}`);
  };

  const handleAdd = () => {
    navigate("/addlession");
  };



  const handleDeleteLesson = async (lessonId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");

      // Make an API call to delete the lesson
      const response = await fetch(
        `${apiUrl}/OfflineLessoon/Id?LessonId=${lessonId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error deleting lesson: ${response.status}`);
      }

      // If the API call is successful, update the local state
      setLesson((prevLessons) =>
        prevLessons.filter((lesson) => lesson.lessonId !== lessonId)
      );
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred while deleting the lesson. Please try again later.");
      setSnackbarMessage('An error occurred while deleting the lesson. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error") ;
    } finally {
      setShowModal(false);
      setSnackbarMessage('Lesson deleted successfully!');
      setOpenSnackbar(true);
      setSnackbarSeverity("success") ;
    }
  };





  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredLessons = lesson.filter(
    (lesson) =>
      lesson.lessonName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.className.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.lessonContent.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  const paginatedData = filteredLessons.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );



  return (
    <Container maxWidth="lg" >

      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Lesson Details
        </Typography> </Box>

      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>




        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            label="Search Lessons"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearch}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}


          />
          <Tooltip title={!canAdd && "You are not Authorized to Add"} arrow><span>

            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}

              style={{
                width: "160px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}
              disabled={!canAdd}  >
              Add Lesson
            </Button></span></Tooltip>
        </Box>


        {loading && (
          <Fade in={loading}>
            <CircularProgress sx={{ display: "block", mx: "auto", mb: 2 }} />
          </Fade>
        )}
        <Fade in={!loading}>
          <TableContainer component={Paper}>
            <Table
              sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc',
                  fontSize: '1rem',
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    Lesson Name
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    Class
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    Content
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLessons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((lesson) => (
                    <TableRow
                      key={lesson.lessonId}
                      sx={{
                        borderTop: '1px solid #ccc',
                        // "&:hover": {
                        //   backgroundColor: "#f5f5f5",
                        //   transition: "background-color 0.2s",
                        // },
                      }}
                    >
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{lesson.lessonName}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{lesson.className}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{lesson.lessonContent}</TableCell>
                      <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                        <Tooltip title={!canEdit && "You are not Authorized to Edit"} arrow><span>

                          <Button
                            variant="contained"
                            color="warning"
                            onClick={() => handleEditLesson(lesson.lessonId)}
                            disabled={!canEdit}
                            sx={{ marginRight: '8px' }}

                          >
                            Edit
                          </Button></span></Tooltip>
                        <Tooltip title={!canDelete && "You are not Authorized to Delete"} arrow><span>

                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setLessonIdToDelete(lesson.lessonId); // Set the lessonId to delete
                              setShowModal(true); // Open the modal
                            }}
                            disabled={!canDelete}
                          >
                            Delete
                          </Button></span></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </Fade>
        {filteredLessons.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredLessons.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredLessons.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

      </Paper>

      <Modal open={showModal} onClose={() => { setShowModal(false) }} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete this lesson {lesson.lessonName}?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => { setShowModal(false) }} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  handleDeleteLesson(lessonIdToDelete); // Use the stored lessonId
                }}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default LessonPage;
